/**
 * CostLineItemPicker — Costing line typeahead, so a fabric/trim/packing
 * line can link to a real inventory item instead of staying free text.
 *
 * Mirror of `features/purchase-orders/components/po-item-picker.tsx`
 * (copied rather than imported cross-feature — the "Sibling Importer"
 * anti-pattern), simplified: a cost line has no HSN/tax, just label · uom ·
 * rate. Linking matters downstream — the Sourcing screen's on-hand stock
 * and rate-history features only work for BOM lines that carry a real
 * `itemId`, which only ever comes from a pick here.
 *
 * Picking is never required — free-form lines stay legal for materials
 * that aren't catalogued yet. The picker is a shortcut.
 *
 * @see apps/web/src/features/purchase-orders/components/po-item-picker.tsx
 */
import { useEffect, useRef, useState } from "react";
import { Package, Search, X } from "lucide-react";
import { DialogRoot, DialogContent, DialogTitle } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";

/** Hydration payload for a cost line — string-typed to match `EditorLine`. */
export interface PickedCostItemValues {
  itemId: string;
  label: string;
  uom: string;
  /** Rate in rupees, e.g. `'240'` or `'15.50'`. Empty string = no default. */
  rate: string;
}

export interface CostLineItemPickerProps {
  onPick: (values: PickedCostItemValues) => void;
  disabled?: boolean | undefined;
}

export function CostLineItemPicker({ onPick, disabled }: CostLineItemPickerProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 200);
    return () => clearTimeout(t);
  }, [input]);

  const list = trpc.items.list.useQuery(
    {
      limit: 20,
      tab: "all",
      usage: "purchase",
      ...(debounced ? { q: debounced } : {}),
    },
    { enabled: open },
  );

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  const items = list.data?.items ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label="Pick item from catalogue"
        title="Pick from item catalogue"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted hover:bg-raised hover:text-ink disabled:cursor-not-allowed disabled:opacity-30"
      >
        <Package size={14} aria-hidden />
      </button>

      <DialogRoot open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[70vh] flex-col overflow-hidden p-0 sm:max-w-[480px]">
          <DialogTitle className="sr-only">Pick item from purchase catalogue</DialogTitle>

          <label className="flex items-center gap-2 border-b border-line px-3 py-3 text-sm">
            <Search size={14} className="shrink-0 text-muted" aria-hidden />
            <input
              ref={inputRef}
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Name or SKU…"
              className="flex-1 bg-transparent outline-none placeholder:text-muted"
            />
            {input ? (
              <button
                type="button"
                onClick={() => setInput("")}
                aria-label="Clear search"
                className="text-muted hover:text-ink"
              >
                <X size={12} aria-hidden />
              </button>
            ) : null}
          </label>

          <div className="flex-1 overflow-y-auto">
            {list.isLoading ? (
              <p className="p-4 text-sm text-muted">Loading…</p>
            ) : list.error ? (
              <p className="p-4 text-sm text-bad">{list.error.message}</p>
            ) : items.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-sm font-medium">
                  {debounced ? "No items match." : "No purchase items yet."}
                </p>
                <p className="mt-1 text-xs text-muted">
                  {debounced
                    ? "Try a name prefix or paste the SKU — or just leave this line as free text."
                    : 'Tag items in /items with usage "purchase" or "both" to use this picker.'}
                </p>
              </div>
            ) : (
              <ul role="listbox" aria-label="Purchase item catalogue">
                {items.map((item) => {
                  const ratePaise = item.defaultRatePaise ? BigInt(item.defaultRatePaise) : null;
                  const ratePreview = ratePaise === null ? "no default rate" : `₹${paisePreview(ratePaise)}/${item.unit}`;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onPick({
                            itemId: item.id,
                            label: item.name,
                            uom: item.unit,
                            rate: ratePaise === null ? "" : paiseToRupeeString(ratePaise),
                          });
                          setOpen(false);
                          setInput("");
                        }}
                        className="w-full border-b border-line/50 px-3 py-2.5 text-left last:border-b-0 hover:bg-raised focus:bg-raised focus:outline-none"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm font-medium text-ink-2">{item.name}</span>
                          {item.sku ? <span className="shrink-0 font-mono text-xs text-muted">{item.sku}</span> : null}
                        </div>
                        <div className="mt-0.5 text-xs text-muted">
                          {ratePreview}
                          {item.usage === "both" ? (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-raised px-1.5 text-xs font-medium uppercase tracking-wide text-ink-2">
                              both
                            </span>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </DialogRoot>
    </>
  );
}

/** `₹1,420` from `142000n`. Indian-locale grouping. */
function paisePreview(paise: bigint): string {
  const rupees = paise / 100n;
  return rupees.toLocaleString("en-IN");
}

/** `'15'` or `'15.50'` — the form-schema's decimal-string shape. */
function paiseToRupeeString(paise: bigint): string {
  const rupees = paise / 100n;
  const remainder = paise % 100n;
  if (remainder === 0n) return rupees.toString();
  return `${rupees}.${String(remainder).padStart(2, "0")}`;
}
